import { FunctionComponent, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { faChartLineUp } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";
import { useGetFrameworkEffectivenessSummaryQuery } from "@/pages/Private/redux/frameworkEffectiveness/frameworkEffectiveness.api";

import { FrameworkOverrides } from "./types";

interface FrameworkEffectivenessChipProps {
	frameworkOverrides?: FrameworkOverrides;
	personaCategory?: string | null;
	companyCategory?: string | null;
}

const TOP_N_DISPLAY = 3;

export const FrameworkEffectivenessChip: FunctionComponent<FrameworkEffectivenessChipProps> = ({
	frameworkOverrides,
	personaCategory,
	companyCategory,
}) => {
	const { t } = useTranslation();

	const allOverridden = useMemo(() => {
		if (!frameworkOverrides) {
			return false;
		}

		return (
			frameworkOverrides.step1FrameworkId !== null &&
			frameworkOverrides.step2FrameworkId !== null &&
			frameworkOverrides.step3FrameworkId !== null
		);
	}, [frameworkOverrides]);

	const queryParams = useMemo(
		() => ({
			...(personaCategory ? { personaCategory } : {}),
			...(companyCategory ? { companyCategory } : {}),
		}),
		[personaCategory, companyCategory]
	);

	const { data, isLoading, isError } = useGetFrameworkEffectivenessSummaryQuery(queryParams, {
		skip: allOverridden,
	});

	if (allOverridden || isLoading || isError) {
		return null;
	}

	if (!data || data.scope === "none" || data.items.length === 0) {
		return null;
	}

	const top = data.items.slice(0, TOP_N_DISPLAY);
	const scopeLabel =
		data.scope === "persona_company"
			? t("messages.generateBar.effectiveness.scopePersonaCompany")
			: t("messages.generateBar.effectiveness.scopeCompany");

	return (
		<div
			aria-label={t("messages.generateBar.effectiveness.ariaLabel") || undefined}
			className="rounded-lg border border-border-light bg-brand-white px-3 py-2 flex items-center gap-2 text-xs"
			data-testid="framework-effectiveness-chip"
		>
			<Icon className="text-text-meta" icon={faChartLineUp} />
			<span className="font-semibold text-text">
				{t("messages.generateBar.effectiveness.heading")}
			</span>
			<span className="text-text-meta">{scopeLabel}</span>
			<ul className="flex items-center gap-2 flex-wrap">
				{top.map((item) => (
					<li
						className="rounded-md border border-border-light px-2 py-0.5 flex items-center gap-1"
						key={item.frameworkName}
					>
						<span className="text-text">{item.frameworkName}</span>
						<span className="text-text-meta">{item.replyRatePct}%</span>
					</li>
				))}
			</ul>
		</div>
	);
};
